// Сопоставление строк чека с меню сети.
//
// ПОЧЕМУ НЕ ПРЯМОЕ СРАВНЕНИЕ. В чеке названия сокращены и слиты:
// «КомбоБигМаэстро», «Стандарт Фри», «Байтс 63 гр.», «0.4КолаБезсахар».
// В официальном меню те же позиции называются «Биг Маэстро бургер оригинальный»
// и «Картофель фри стандартный». Поэтому сравниваем по корням слов.
//
// ПРАВИЛО КОМБО (главное). Комбо в чеке печатается родителем, содержимое —
// с отступом. Считать и то и другое нельзя, калории удвоятся. При этом у комбо
// порции обычно урезаны: «Пати бокс» целиком — 318 ккал, а его содержимое
// по отдельности даёт почти 400. Значит: НАШЛИ РОДИТЕЛЯ В МЕНЮ — берём его,
// детей выключаем. НЕ нашли — берём детей.

export type ReceiptLine = {
  name: string;
  qty: number;
  level: number;
  is_modifier: boolean;
};

export type MenuItem = {
  id: string;
  name: string;
  weight_g: number;
  kcal: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
};

export type MatchedLine = {
  line: ReceiptLine;
  index: number;
  /** Лучшее совпадение и запасные варианты — человек может переключить */
  candidates: { item: MenuItem; score: number }[];
  chosenId: string | null;
  /** Выключено, потому что уже посчитано в составе комбо выше */
  coveredByParent: boolean;
};

const STOP = new Set([
  'шт', 'гр', 'г', 'мл', 'л', 'комбо', 'набор', 'меню', 'ланч',
  'с', 'и', 'в', 'на', 'по', 'из', 'для',
]);

/** «КомбоБигМаэстро» → ['комбо','биг','маэстро'] */
export function tokenize(raw: string): string[] {
  return raw
    .replace(/ё/gi, 'е')
    // разрезаем слипшиеся слова по границе строчная→Заглавная
    .replace(/([а-яa-z])([А-ЯA-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-zа-я0-9]+/gi, ' ')
    .split(' ')
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !STOP.has(t));
}

/** Срезает русские окончания: «малая» и «малый» → «мал» */
function stem(w: string): string {
  if (w.length <= 4) return w;
  return w.replace(/(ыми|ими|ого|его|ому|ему|ая|яя|ое|ее|ые|ие|ый|ий|ой|ей|ым|им|ом|ем|ах|ях|ов|ев|у|ю|а|я|ы|и|е|о)$/, '');
}

/** Общий корень: «малая» и «малый» — для наших целей одно слово */
function sameRoot(a: string, b: string): boolean {
  if (a === b) return true;
  const sa = stem(a);
  const sb = stem(b);
  if (sa === sb) return true;
  const n = Math.min(sa.length, sb.length);
  if (n < 4) return false;
  return sa.slice(0, n) === sb.slice(0, n);
}

/** «ШефРОЛЛ» → «шефролл»: слитная форма, чтобы ловить слипшиеся названия */
function squash(s: string): string {
  return s.replace(/ё/gi, 'е').toLowerCase().replace(/[^a-zа-я]/gi, '');
}

// Слова, по которым видно, что позиция — набор, а не одно блюдо
const SET_WORDS = ['баскет', 'бокс', 'комбо', 'сет', 'набор', 'ланч', 'пати', 'дуэт'];

function looksLikeSet(name: string): boolean {
  const s = squash(name);
  return SET_WORDS.some((w) => s.includes(w));
}

/**
 * Доля слов из чека, нашедшихся в названии меню.
 * Числа («0.4», «63») сознательно не учитываем — в чеке это объём и вес,
 * в меню их пишут иначе, и они только шумят.
 */
export function score(receiptName: string, menuName: string): number {
  const rt = tokenize(receiptName).filter((t) => !/^\d+$/.test(t));
  const mt = tokenize(menuName);
  if (!rt.length || !mt.length) return 0;

  let hit = 0;
  for (const t of rt) if (mt.some((m) => sameRoot(t, m))) hit++;
  const coverage = hit / rt.length;

  // Небольшой бонус, если и меню почти целиком покрыто: отсекает случаи,
  // когда короткий запрос «Фри» одинаково хорошо липнет к десятку позиций
  let back = 0;
  for (const m of mt) if (rt.some((t) => sameRoot(t, m))) back++;
  const base = coverage * 0.8 + (back / mt.length) * 0.2;

  // «ШефРОЛЛ» против «Шефролл оригинальный»: по словам не сходится, потому что
  // в чеке слово слитное. Слитная форма это ловит — и это сильный сигнал.
  const rs = squash(receiptName);
  const ms = squash(menuName);
  if (rs.length >= 5 && ms.includes(rs)) return Math.max(base, 0.95);
  if (ms.length >= 5 && rs.includes(ms)) return Math.max(base, 0.9);

  return base;
}

const MIN_SCORE = 0.5;

/** Ищет кандидатов для одной строки чека */
export function findCandidates(name: string, menu: MenuItem[], limit = 4) {
  return menu
    .map((item) => ({ item, score: score(name, item.name) }))
    .filter((c) => c.score >= MIN_SCORE)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Разбирает чек целиком: сопоставляет строки и применяет правило комбо.
 * Модификаторы («СО ЛЬДОМ») выбрасываются — это не еда.
 */
export function matchReceipt(lines: ReceiptLine[], menu: MenuItem[]): MatchedLine[] {
  const food = lines.filter((l) => !l.is_modifier);

  const hasChildren = (i: number) =>
    i + 1 < food.length && food[i + 1].level > food[i].level;

  const matched: MatchedLine[] = food.map((line, index) => {
    let candidates = findCandidates(line.name, menu);

    // Строка с вложенными позициями — это набор. Опасность: «КомбоБигМаэстро»
    // отлично совпадает с одиночным «Биг Маэстро бургер», и тогда родитель
    // проглотил бы своих детей вместе с картошкой и напитком.
    // Поэтому набору разрешаем совпасть только с набором же («Пати бокс»).
    if (hasChildren(index)) {
      candidates = candidates.filter((c) => looksLikeSet(c.item.name));
    }

    return {
      line,
      index,
      candidates,
      chosenId: candidates[0]?.item.id ?? null,
      coveredByParent: false,
    };
  });

  // Правило комбо: если у строки level N нашёлся товар в меню, то все строки
  // ниже с level > N (до следующей строки с level <= N) уже посчитаны в нём.
  for (let i = 0; i < matched.length; i++) {
    const cur = matched[i];
    if (cur.coveredByParent || !cur.chosenId) continue;

    for (let j = i + 1; j < matched.length; j++) {
      if (matched[j].line.level <= cur.line.level) break;
      matched[j].coveredByParent = true;
      matched[j].chosenId = null;
    }
  }

  return matched;
}
